<script setup lang="ts">
  import { computed, ref, watch, watchEffect } from 'vue'
  import { AlertTriangle, Info, X } from 'lucide-vue-next'
  import { Button } from '@/components/ui/button'
  import { useTranslation } from '@/composables/useTranslation'
  import {
    addPhoneAiCalendarDays,
    formatPhoneAiBannerDayMonth,
    getPhoneAiRemainingMinutes,
    getPhoneAiWarningTier,
    isPhoneAiBeginnerPlan,
    parsePhoneAiPeriodDate,
    type PhoneAiWarningTier
  } from '@/utils/phoneAiUsage'
  import { useMetrics, MetricEventType } from '@/composables/useMetrics'

  const props = withDefaults(
    defineProps<{
      usedMinutes?: number
      includedMinutes?: number
      /** `phonebot_self_service_plan` from org */
      plan?: string | null
      /** `phonebot_self_service_current_period_end` */
      periodEnd?: string | null
    }>(),
    {
      usedMinutes: 0,
      includedMinutes: 0,
      plan: null,
      periodEnd: null
    }
  )

  const { t, locale } = useTranslation({
    en: {
      planProfessional: 'Professional plan',
      planProfessionalPlus: 'Professional Plus plan',
      planEnterprise: 'Enterprise plan',
      beginnerRemaining:
        'Only {remaining} Phone AI minutes left until {periodEnd}. Upgrade your plan to avoid deactivation.',
      beginnerRemainingNoDate:
        'Only {remaining} Phone AI minutes left. Upgrade your plan to avoid deactivation.',
      beginnerLimit:
        'Phone AI is inactive and will be available again on {renewalDate}. Upgrade your plan to restore service immediately.',
      beginnerLimitNoDate:
        'Phone AI is inactive after included minutes are used up. Upgrade your plan to restore service immediately.',
      proRemaining:
        'You still have {remaining} included minutes in your {planName} until {periodEnd}.',
      proRemainingNoDate: 'You still have {remaining} included minutes in your {planName}.',
      proLimit: 'All included minutes are used up and will renew on {renewalDate}.',
      proLimitNoDate:
        'All included minutes are used up; they will renew at the next billing cycle.',
      dismissLabel: 'Dismiss'
    },
    de: {
      planProfessional: 'Professional-Tarif',
      planProfessionalPlus: 'Professional-Plus-Tarif',
      planEnterprise: 'Enterprise-Tarif',
      beginnerRemaining:
        'Noch {remaining} Minuten Telefon-KI verfügbar bis zum {periodEnd}. Werten Sie Ihren Tarif auf, um eine Deaktivierung zu vermeiden.',
      beginnerRemainingNoDate:
        'Noch {remaining} Minuten Telefon-KI verfügbar. Werten Sie Ihren Tarif auf, um eine Deaktivierung zu vermeiden.',
      beginnerLimit:
        'Die Telefon-KI ist inaktiv und wird am {renewalDate} wieder aktiviert. Werten Sie Ihren Tarif auf, um die Leistung sofort wieder zu aktivieren.',
      beginnerLimitNoDate:
        'Die Telefon-KI ist inaktiv, nachdem die Inklusivminuten aufgebraucht sind. Werten Sie Ihren Tarif auf, um die Leistung sofort wieder zu aktivieren.',
      proRemaining:
        'Noch {remaining} enthaltene Minuten verfügbar in Ihrem {planName} bis zum {periodEnd}.',
      proRemainingNoDate: 'Noch {remaining} enthaltene Minuten verfügbar in Ihrem {planName}.',
      proLimit: 'Alle enthaltene Minuten sind aufgebraucht und werden am {renewalDate} erneuert.',
      proLimitNoDate:
        'Alle enthaltene Minuten sind aufgebraucht; sie werden im nächsten Abrechnungszeitraum erneuert.',
      dismissLabel: 'Schließen'
    },
    fr: {
      planProfessional: 'forfait Professional',
      planProfessionalPlus: 'forfait Professional Plus',
      planEnterprise: 'forfait Enterprise',
      beginnerRemaining:
        'Encore {remaining} minutes d’IA téléphonique jusqu’au {periodEnd}. Passez à un forfait supérieur pour éviter une désactivation.',
      beginnerRemainingNoDate:
        'Encore {remaining} minutes d’IA téléphonique. Passez à un forfait supérieur pour éviter une désactivation.',
      beginnerLimit:
        'L’IA téléphonique est inactive et sera à nouveau disponible le {renewalDate}. Passez à un forfait supérieur pour réactiver immédiatement.',
      beginnerLimitNoDate:
        'L’IA téléphonique est inactive après épuisement des minutes incluses. Passez à un forfait supérieur pour réactiver immédiatement.',
      proRemaining:
        'Il vous reste {remaining} minutes incluses dans votre {planName} jusqu’au {periodEnd}.',
      proRemainingNoDate: 'Il vous reste {remaining} minutes incluses dans votre {planName}.',
      proLimit:
        'Toutes les minutes incluses sont épuisées ; elles seront renouvelées le {renewalDate}.',
      proLimitNoDate:
        'Toutes les minutes incluses sont épuisées ; elles seront renouvelées au prochain cycle de facturation.',
      dismissLabel: 'Fermer'
    }
  })

  const tier = computed<PhoneAiWarningTier>(() =>
    getPhoneAiWarningTier(props.usedMinutes, props.includedMinutes)
  )

  const visible = computed(() => tier.value !== 'none')

  const remainingMinutes = computed(() =>
    getPhoneAiRemainingMinutes(props.usedMinutes, props.includedMinutes)
  )

  const periodEndDate = computed(() => parsePhoneAiPeriodDate(props.periodEnd))

  const periodEndFormatted = computed(() => {
    const d = periodEndDate.value
    if (!d) {
      return ''
    }
    return formatPhoneAiBannerDayMonth(d, locale.value)
  })

  const renewalDateFormatted = computed(() => {
    const end = periodEndDate.value
    if (!end) {
      return ''
    }
    const next = addPhoneAiCalendarDays(end, 1)
    return formatPhoneAiBannerDayMonth(next, locale.value)
  })

  const planNameLabel = computed(() => {
    const p = String(props.plan ?? '')
    if (p === 'phone_professional_plan') {
      return t('planProfessional')
    }
    if (p === 'phone_professional_plus_plan') {
      return t('planProfessionalPlus')
    }
    if (p === 'phone_enterprise_plan') {
      return t('planEnterprise')
    }
    return t('planProfessional')
  })

  const isBeginner = computed(() => isPhoneAiBeginnerPlan(props.plan))

  const visualVariant = computed<'beginner' | 'pro'>(() => {
    if (isBeginner.value) {
      return 'beginner'
    }
    return 'pro'
  })

  const bannerClass = computed(() => {
    if (visualVariant.value === 'beginner') {
      return 'border-red-100 bg-red-50 text-red-950'
    }
    return 'border-blue-100 bg-blue-50 text-blue-950'
  })

  const iconClass = computed(() => {
    if (visualVariant.value === 'beginner') {
      return 'text-red-700'
    }
    return 'text-blue-700'
  })

  const dismissButtonClass = computed(() => {
    if (visualVariant.value === 'beginner') {
      return 'text-red-700 hover:bg-red-100 hover:text-red-900'
    }
    return 'text-blue-700 hover:bg-blue-100 hover:text-blue-900'
  })

  const showInfoIcon = computed(() => visualVariant.value === 'pro')

  const message = computed(() => {
    const rem = remainingMinutes.value
    const pe = periodEndFormatted.value
    const ren = renewalDateFormatted.value

    if (tier.value === 'limit') {
      if (isBeginner.value) {
        return ren ? t('beginnerLimit', { renewalDate: ren }) : t('beginnerLimitNoDate')
      }
      return ren ? t('proLimit', { renewalDate: ren }) : t('proLimitNoDate')
    }

    if (tier.value === 'info' || tier.value === 'urgent') {
      if (isBeginner.value) {
        return pe
          ? t('beginnerRemaining', { remaining: rem, periodEnd: pe })
          : t('beginnerRemainingNoDate', { remaining: rem })
      }
      return pe
        ? t('proRemaining', { remaining: rem, planName: planNameLabel.value, periodEnd: pe })
        : t('proRemainingNoDate', { remaining: rem, planName: planNameLabel.value })
    }

    return ''
  })

  const dismissed = ref(false)

  watch(
    () =>
      [tier.value, props.usedMinutes, props.includedMinutes, props.periodEnd, props.plan] as const,
    () => {
      dismissed.value = false
    }
  )

  const showBanner = computed(() => visible.value && !dismissed.value)

  const { trackEvent } = useMetrics()
  const lastEmittedKey = ref<string | null>(null)

  // `threshold_percent` mirrors the backend ``PHONE_AI_USAGE_THRESHOLD_REACHED`` event's
  // taxonomy so PostHog funnels can pivot UI-seen → backend-confirmed on a single field.
  // Frontend tiers don't perfectly overlap backend thresholds: backend fires at 30/90/100,
  // frontend banner appears at 80/90/100. Both systems agree at the critical 90/100 points,
  // which is where the funnel matters most.
  const tierToThresholdPercent: Record<PhoneAiWarningTier, number | null> = {
    none: null,
    info: 80,
    urgent: 90,
    limit: 100
  }

  // Dedup is deliberately coarse — keyed on tier + plan + periodEnd — so a parent
  // re-fetch that nudges `usedMinutes` by one (poll tick, new call completing) does
  // not re-emit the same tier. Product intent is "user saw this tier this period",
  // not "user saw this exact minute count"; the minute counts remain in the payload
  // for dashboards that need them.
  watchEffect(() => {
    if (!showBanner.value) return
    if (tier.value === 'none') return
    const key = `${tier.value}:${props.plan ?? ''}:${props.periodEnd ?? ''}`
    if (lastEmittedKey.value === key) return
    lastEmittedKey.value = key
    trackEvent(MetricEventType.PHONE_AI_WARNING_SHOWN, {
      tier: tier.value,
      threshold_percent: tierToThresholdPercent[tier.value],
      plan: props.plan ?? null,
      used_minutes: props.usedMinutes,
      included_minutes: props.includedMinutes,
      is_beginner: isBeginner.value,
      billing_period_end: props.periodEnd ?? null
    })
  })
</script>

<template>
  <div
    v-if="showBanner"
    class="flex items-center gap-2 rounded-lg border px-2 py-2"
    :class="bannerClass"
    role="alert"
  >
    <Info
      v-if="showInfoIcon"
      class="size-4 shrink-0"
      :class="iconClass"
      aria-hidden="true"
    />
    <AlertTriangle
      v-else
      class="size-4 shrink-0"
      :class="iconClass"
      aria-hidden="true"
    />
    <p class="min-w-0 flex-1 text-xs font-medium leading-4">
      {{ message }}
    </p>
    <Button
      type="button"
      variant="ghost"
      size="icon"
      class="size-8 shrink-0"
      :class="dismissButtonClass"
      :aria-label="t('dismissLabel')"
      @click="dismissed = true"
    >
      <X class="size-4" />
    </Button>
  </div>
</template>
